/** Display names + short definitions for Strategy R&D Concepts composer. */

const FULL_NAMES: Record<string, string> = {
  session_first_candle: "Session opening range",
  previous_day_high_low: "Previous day high / low",
  pdh_pdl: "Previous day high / low",
  pwh_pwl: "Prior week high / low",
  pmh_pml: "Prior month high / low",
  swing_hl: "Swing high / low",
  developing_day_hl: "Developing day high / low",
  bullish_engulfing: "Bullish engulfing",
  bearish_engulfing: "Bearish engulfing",
  bull_engulf: "Bullish engulfing",
  bear_engulf: "Bearish engulfing",
  inside_bar: "Inside bar",
  engulf: "Engulfing",
  pin_bar: "Pin bar",
  ob: "Order block",
  pct_break_bar: "Percent break bar",
  stage: "Stage analysis",
  stage_2: "Stage 2 (advancing)",
  stage_4: "Stage 4 (declining)",
  fvg_bull: "Bullish fair value gap",
  fvg_bear: "Bearish fair value gap",
  order_block_bull: "Bullish order block",
  order_block_bear: "Bearish order block",
  order_block: "Order block",
  breaker: "Breaker block",
  breaker_bull: "Bullish breaker block",
  breaker_bear: "Bearish breaker block",
  liquidity_sweep: "Liquidity sweep",
  liq_sweep: "Liquidity sweep",
  liq_run: "Liquidity run",
  volume_spike: "Volume spike",
  trend_aligned: "Trend aligned",
  inside_bar_cluster: "Inside bar cluster",
  inside_cluster: "Inside bar cluster",
  atr_squeeze_secondary: "ATR squeeze",
  coil_atr_squeeze: "ATR squeeze",
  coil_range_contract: "Range contraction coil",
  clear_of_opposing_sr: "Clear of opposing Support/Resistance (in quartiles)",
  near_ob: "Near order block",
  near_breaker: "Near breaker block",
  near_swing: "Near swing high/low",
  near_prior_sr: "Near prior day/week/month High/Low",
  strong_relative_volume: "Strong relative volume",
  rvol_strong: "Strong relative volume",
  volume_expansion: "Volume expansion",
  vol_expansion: "Volume expansion",
  volume_dry_up: "Volume dry-up",
  vol_dryup: "Volume dry-up",
  regime_uptrend: "Regime uptrend",
  regime_trend_up: "Regime uptrend",
  regime_downtrend: "Regime downtrend",
  regime_trend_down: "Regime downtrend",
  regime_range: "Regime range",
  regime_choppy: "Regime choppy",
  above_sma50: "Above 50-period simple moving average",
  below_sma50: "Below 50-period simple moving average",
  above_sma200: "Above 200-period simple moving average",
  below_sma200: "Below 200-period simple moving average",
  above_ema10: "Above 10-period exponential moving average",
  below_ema10: "Below 10-period exponential moving average",
  above_ema20: "Above 20-period exponential moving average",
  below_ema20: "Below 20-period exponential moving average",
  prior_liq_sweep: "Liquidity sweep",
  prior_liq_run: "Liquidity run",
  fvg_overlap: "Overlaps / near a Fair Value Gap",
  bos: "Break of structure",
  choch: "Change of character",
  htf_bias: "Higher timeframe bias",
  pack_orb: "Session opening range breakout",
  pack_orb_break: "Session opening range breakout",
  pack_pdh_pdl: "Previous day high / low retest",
  rsi_div: "RSI divergence",
  rsi_div_bull: "RSI bullish divergence",
  rsi_div_bear: "RSI bearish divergence",
  macd_div: "MACD divergence",
  macd_div_bull: "MACD bullish divergence",
  macd_div_bear: "MACD bearish divergence",
  macd_cross: "MACD signal cross",
  macd_hist_up: "MACD histogram above zero",
  macd_hist_down: "MACD histogram below zero",
  macd_momentum: "MACD momentum",
  macd_trend: "MACD trend",
  macd_improving: "MACD momentum improving",
  macd_cross_bull: "MACD bullish cross",
  macd_cross_bear: "MACD bearish cross",
  macd_zero: "MACD zero cross",
  macd_zero_bull: "MACD zero bull",
  macd_zero_bear: "MACD zero bear",
  stoch_div: "Stochastic divergence",
  stoch_div_bull: "Stochastic bullish divergence",
  stoch_div_bear: "Stochastic bearish divergence",
};

/** Compact chart pills — full names stay in the composer. */
const CHART_ABBREVS: Record<string, string> = {
  choch: "CHoCH",
  bos: "BOS",
  fvg: "FVG",
  fvg_bull: "FVG",
  fvg_bear: "FVG",
  fvg_overlap: "FVG+",
  ifvg: "IFVG",
  near_ob: "OB",
  near_breaker: "BB",
  near_swing: "Swing",
  near_prior_sr: "Prior",
  order_block: "OB",
  order_block_bull: "OB",
  order_block_bear: "OB",
  ob: "OB",
  ob_bull: "OB",
  ob_bear: "OB",
  breaker: "BB",
  breaker_bull: "BB",
  breaker_bear: "BB",
  prior_liq_sweep: "Liq",
  prior_liq_run: "Run",
  liq_sweep: "Liq",
  liq_run: "Run",
  liquidity_sweep: "Liq",
  trendline: "TL",
  regime: "R",
  regime_trend: "R",
  regime_trend_down: "R↓",
  regime_downtrend: "R↓",
  regime_trend_up: "R↑",
  regime_uptrend: "R↑",
  regime_range: "R↔",
  regime_choppy: "R~",
  coil_range_contract: "Coil",
  coil_atr_squeeze: "ATR",
  atr_squeeze_secondary: "ATR",
  clear_of_opposing_sr: "S/R",
  inside_cluster: "IB",
  inside_bar_cluster: "IB",
  inside_bar: "IB",
  pct_break_bar: "%Brk",
  stage: "Stage",
  stage_2: "S2",
  stage_4: "S4",
  pin_bar: "Pin",
  pin_bar_bull: "Pin↑",
  pin_bar_bear: "Pin↓",
  engulf: "Eng",
  bull_engulf: "Eng",
  bear_engulf: "Eng",
  bullish_engulfing: "Eng",
  bearish_engulfing: "Eng",
  doji: "Doji",
  doji_dragonfly: "Doji↑",
  doji_gravestone: "Doji↓",
  hammer: "Ham",
  hanging_man: "Hang",
  inverted_hammer: "InvH",
  shooting_star: "Star",
  hammer_star: "Ham",
  marubozu: "Maru",
  marubozu_bull: "Maru↑",
  marubozu_bear: "Maru↓",
  harami: "Har",
  harami_bull: "Har↑",
  harami_bear: "Har↓",
  harami_cross: "HarX",
  harami_cross_bull: "HarX↑",
  harami_cross_bear: "HarX↓",
  tweezers: "Twz",
  tweezer_bottom: "Twz↑",
  tweezer_top: "Twz↓",
  piercing: "Prc",
  dark_cloud: "DkC",
  cloud_pierce: "Prc",
  morning_star: "M★",
  evening_star: "E★",
  star_triple: "★",
  three_white_soldiers: "3WS",
  three_black_crows: "3BC",
  three_soldiers: "3Sol",
  three_inside: "3In",
  three_inside_up: "3In↑",
  three_inside_down: "3In↓",
  head_shoulders: "Head & Shoulders",
  inv_head_shoulders: "Inv. Head & Shoulders",
  head_shoulders_family: "Head & Shoulders",
  double_top: "Double top",
  double_bottom: "Double bottom",
  double_extreme: "Double",
  triple_top: "Triple top",
  triple_bottom: "Triple bottom",
  triple_extreme: "Triple",
  rising_wedge: "Wedge",
  falling_wedge: "Wedge",
  wedge: "Wedge",
  cup_handle: "Cup & Handle",
  inv_cup_handle: "Inv. Cup & Handle",
  cup_handle_family: "Cup & Handle",
  flag: "Flag",
  flag_bull: "Flag↑",
  flag_bear: "Flag↓",
  pennant: "Pennant",
  pennant_bull: "Pennant↑",
  pennant_bear: "Pennant↓",
  triangle: "Triangle",
  triangle_asc: "Ascending",
  triangle_desc: "Descending",
  triangle_sym: "Symmetrical",
  rectangle: "Rectangle",
  rsi: "RSI",
  rsi_oversold: "RSI↓",
  rsi_overbought: "RSI↑",
  rsi_neutral: "RSI",
  rsi_div: "RSId",
  rsi_div_bull: "RSId↑",
  rsi_div_bear: "RSId↓",
  macd: "MACD",
  macd_hist_up: "Hist+",
  macd_hist_down: "Hist−",
  macd_momentum: "MACD mom",
  macd_trend: "MACD trend",
  macd_improving: "MACD↗",
  macd_cross: "MACDx",
  macd_cross_bull: "MACDx↑",
  macd_cross_bear: "MACDx↓",
  macd_zero: "MACD0",
  macd_zero_bull: "MACD0↑",
  macd_zero_bear: "MACD0↓",
  macd_div: "MACDd",
  macd_div_bull: "MACDd↑",
  macd_div_bear: "MACDd↓",
  stoch: "Stoch",
  stoch_oversold: "Stoch↓",
  stoch_overbought: "Stoch↑",
  stoch_div: "Stochd",
  stoch_div_bull: "Stochd↑",
  stoch_div_bear: "Stochd↓",
  mfi: "MFI",
  mfi_oversold: "MFI↓",
  mfi_overbought: "MFI↑",
  adx: "ADX",
  adx_weak: "ADX↓",
  adx_developing: "ADX~",
  adx_strong: "ADX↑",
  adx_very_strong: "ADX↑↑",
  atr_regime: "ATR",
  atr_regime_very_low: "ATR↓↓",
  atr_regime_low: "ATR↓",
  atr_regime_medium: "ATR~",
  atr_regime_high: "ATR↑",
  atr_regime_very_high: "ATR↑↑",
  bb_zone: "BB",
  bb_zone_lower_extreme: "BB↓↓",
  bb_zone_lower: "BB↓",
  bb_zone_mid: "BB~",
  bb_zone_upper: "BB↑",
  bb_zone_upper_extreme: "BB↑↑",
  bb_squeeze: "BBsq",
  bb_close_outside: "BBout",
  bb_close_outside_bull: "BBout↑",
  bb_close_outside_bear: "BBout↓",
  sma: "SMA",
  sma50: "SMA50",
  sma200: "SMA200",
  sma_cross: "SMAx",
  ema: "EMA",
  ema10: "EMA10",
  ema20: "EMA20",
  ema_cross: "EMAx",
  above_sma50: "SMA50",
  below_sma50: "SMA50",
  above_sma200: "SMA200",
  below_sma200: "SMA200",
  above_ema10: "EMA10",
  below_ema10: "EMA10",
  above_ema20: "EMA20",
  below_ema20: "EMA20",
  killzone: "KZ",
  session_first_candle: "OR",
  pdh_pdl: "PDH",
  previous_day_high_low: "PDH",
  pwh_pwl: "PWH",
  pmh_pml: "PMH",
  swing_hl: "Swing",
  developing_day_hl: "DH/L",
  htf_bias: "Bias",
  own_momentum: "Mom",
  rs_vs_benchmark: "RS",
  mansfield_rs: "Mans",
  atr_ext_from_ma: "ATRx",
  adr_pct: "ADR%",
  pullback_to_ma: "PBtoMA",
  donchian_break: "Donch",
  donchian_break_bull: "Donch↑",
  donchian_break_bear: "Donch↓",
  ttm_squeeze: "TTM",
  volume_spike: "Vol",
  rvol_strong: "RVol",
  strong_relative_volume: "RVol",
  vol_expansion: "Vol+",
  volume_expansion: "Vol+",
  vol_dryup: "Vol-",
  volume_dry_up: "Vol-",
  trend_aligned: "Tr",
};

const SHORT_DEFS: Record<string, string> = {
  session_first_candle:
    "Marks the first candle of the chosen session as the opening range. Level picks high/low/both; Trade side is the entry branch. Set independently.",
  previous_day_high_low:
    "Uses yesterday’s daily high and low. Level filters which break fires; Trade side is independent (e.g. High + Short).",
  pdh_pdl:
    "Uses yesterday’s daily high and low. Level filters which break fires; Trade side is independent (e.g. High + Short).",
  pwh_pwl:
    "Uses last week’s high and low. Level filters which break fires; Trade side is independent (e.g. High + Short).",
  pmh_pml:
    "Uses last month’s high and low. Level filters which break fires; Trade side is independent (e.g. Low + Long).",
  swing_hl:
    "Uses a fractal swing high or low. Level filters which break fires; Trade side is independent.",
  developing_day_hl:
    "Uses the current day’s developing high and low. Level filters which break fires; Trade side is independent.",
  pack_pdh_pdl:
    "Trades around yesterday’s high/low. Presets Entry to “retest then close”: a candle must close beyond the level (break), then a later candle holds the retest and that close is the entry.",
  bullish_engulfing: "A down candle fully engulfed by the next up candle. Bullish candle setup.",
  bearish_engulfing: "An up candle fully engulfed by the next down candle. Bearish candle setup.",
  bull_engulf: "A down candle fully engulfed by the next up candle. Bullish candle setup.",
  bear_engulf: "An up candle fully engulfed by the next down candle. Bearish candle setup.",
  inside_bar: "A bar whose range sits inside the prior (mother) bar. Often used for breakout.",
  pct_break_bar:
    "A shock/expansion bar: |close − prior close| / |prior close| ≥ Min %. Up → Long, down → Short. Levels are the bar high/low for break/retest/rejection.",
  stage:
    "Weinstein Stage analysis: breakout (Stage 1→2 base high / Stage 3→4 base low) or MA regime flip. Prefer Daily or Weekly; SMA 30 on 15m is not the classic 30-week MA.",
  stage_2:
    "Legacy Stage 2 (advancing): prefer family id stage with Trade side Long.",
  stage_4:
    "Legacy Stage 4 (declining): prefer family id stage with Trade side Short.",
  fvg_bull:
    "Bullish fair value gap: the empty price zone between candle 1’s high and candle 3’s low (inside the middle impulse candle).",
  fvg_bear:
    "Bearish fair value gap: the empty price zone between candle 1’s low and candle 3’s high (inside the middle impulse candle).",
  fvg:
    "A fair value gap is an imbalance left by a strong candle. Price often revisits that empty zone.",
  engulf:
    "An engulfing candle fully wraps the previous candle’s range. A common reversal or continuation cue.",
  pin_bar:
    "A pin bar has a long wick and small body. Rejection of a level.",
  ob:
    "An order block is the last opposing candle before a strong impulse. A zone price may revisit.",
  order_block_bull: "Bullish order-block zone: last opposing candle before an impulse move.",
  order_block_bear: "Bearish order-block zone: last opposing candle before an impulse move.",
  order_block:
    "Order-block zone: last opposing candle before an impulse. Pick Trade side for bull / bear / both.",
  breaker:
    "Breaker block: failed order block with flipped role. Signal bar is the failure (when the OB breaks); zone is the origin OB body. Pick Trade side for bull / bear / both.",
  breaker_bull:
    "Bullish breaker block: bearish OB fails; zone flips to support. Signal = failure bar.",
  breaker_bear:
    "Bearish breaker block: bullish OB fails; zone flips to resistance. Signal = failure bar.",
  liquidity_sweep: "Price briefly takes out a nearby swing high/low, then reverses.",
  volume_spike: "Require elevated volume on the setup bar (needs volume data).",
  trend_aligned: "Require the setup to align with a higher-timeframe trend filter.",
  inside_bar_cluster:
    "Require nearby inside bars / compression before the setup, signaling a tighter breakout base.",
  inside_cluster:
    "Require nearby inside bars / compression before the setup, signaling a tighter breakout base.",
  atr_squeeze_secondary:
    "Require local ATR compression before expansion, filtering for quieter bases that can break cleanly.",
  coil_atr_squeeze:
    "Require ATR below its SMA. Quieter base before expansion.",
  coil_range_contract:
    "Require local range contraction (recent window vs prior) before trusting the setup. Primary coil; not ATR.",
  clear_of_opposing_sr:
    "Shorts stay out of the bottom quartile (near support); longs stay out of the top quartile (near resistance). Quartile of the S/R range. Scopes: prior day, week, and/or month highs/lows.",
  near_ob:
    "Require the setup to sit on / overlap a same-side order block.",
  near_breaker:
    "Require the setup near a breaker block. A failed order block with flipped role (broken bull OB → bearish resistance; broken bear OB → bullish support).",
  near_swing:
    "Location: fractal swing high/low (L=R=3 pivots). Not PDH/PWH calendar rails. Respect or Break the swing before entry. Respect is a wick reclaim; Break closes through the swing.",
  near_prior_sr:
    "Location: prior day/week/month H/L (PDH/PDL, PWH/PWL, PMH/PML). Not fractal swings. Respect or Break the nearest selected-scope extreme before entry. Different from Context clear_of_opposing_sr (quartile filter only).",
  strong_relative_volume:
    "Require stronger-than-normal relative volume around the setup so the move is not firing on weak participation.",
  rvol_strong:
    "Require stronger-than-normal relative volume around the setup so the move is not firing on weak participation.",
  volume_expansion:
    "Require volume to expand through the move, confirming participation as price leaves the setup.",
  vol_expansion:
    "Require volume to expand through the move, confirming participation as price leaves the setup.",
  volume_dry_up:
    "Require volume to contract before the move, often used as a pre-breakout compression filter.",
  vol_dryup:
    "Require volume to contract before the move, often used as a pre-breakout compression filter.",
  regime_uptrend:
    "Require the market regime filter to be in an uptrend so bullish setups align with the broader backdrop.",
  regime_trend_up:
    "Require the market regime filter to be in an uptrend so bullish setups align with the broader backdrop.",
  regime_downtrend:
    "Require the market regime filter to be in a downtrend so bearish setups align with the broader backdrop.",
  regime_trend_down:
    "Require the market regime filter to be in a downtrend so bearish setups align with the broader backdrop.",
  regime_range:
    "Require a range regime so the setup is evaluated in a non-trending market backdrop.",
  regime_choppy:
    "Require a choppy regime, useful when testing mean-reverting or non-trend conditions.",
  above_sma50:
    "Require price to be above the 50-period simple moving average as a basic trend-location filter.",
  below_sma50:
    "Require price to be below the 50-period simple moving average as a basic trend-location filter.",
  above_sma200:
    "Require price to be above the 200-period simple moving average.",
  below_sma200:
    "Require price to be below the 200-period simple moving average.",
  above_ema10:
    "Require price to be above the 10-period exponential moving average.",
  below_ema10:
    "Require price to be below the 10-period exponential moving average.",
  above_ema20:
    "Require price to be above the 20-period exponential moving average.",
  below_ema20:
    "Require price to be below the 20-period exponential moving average.",
  prior_liq_sweep:
    "Liquidity sweep: take the pool, then price reverses. Timing chooses before the setup, after it, or either. Confirm only; does not own Enter.",
  prior_liq_run:
    "Liquidity run: take the pool, then price keeps going. Timing chooses before the setup, after it, or either. Confirm only; does not own Enter.",
  liq_sweep:
    "Liquidity sweep: Take the pool, then price reverses (same-bar reclaim or dump within 2 bars). Draw Take + Sweep. Not a Run.",
  liq_run:
    "Liquidity run: Take the pool, then price keeps going (next bar still closes beyond). Draw Take + Run. Never label a trap breakout as Run.",
  fvg_overlap:
    "Require the setup zone to overlap a nearby same-side FVG.",
  bos: "Require a thin break of structure confirmation near the setup. Confirm only; does not own Enter.",
  choch: "Require a thin change of character confirmation near the setup. Confirm only; does not own Enter.",
  rsi_div:
    "Price and RSI disagree at two swings. Classic (reversal) or hidden (continuation). Confirm by comparing the price slope to the RSI slope on the chart.",
  rsi_div_bull:
    "Bullish RSI divergence: price makes a lower low (classic) or higher low (hidden) while RSI does the opposite. Bias long.",
  rsi_div_bear:
    "Bearish RSI divergence: price makes a higher high (classic) or lower high (hidden) while RSI does the opposite. Bias short.",
  macd_div:
    "Price and MACD disagree at two swings. Classic (reversal) or hidden (continuation). Default slope is on the MACD line (Alchemy examples); optional histogram source.",
  macd_div_bull:
    "Bullish MACD divergence: price and MACD slopes disagree in a bullish way. Bias long.",
  macd_div_bear:
    "Bearish MACD divergence: price and MACD slopes disagree in a bearish way. Bias short.",
  macd_cross:
    "MACD line crosses its signal line. Alchemy signal-line crossover. Bull = cross above; bear = cross below.",
  macd_cross_bull: "MACD line crosses above its signal line. Bias long.",
  macd_cross_bear: "MACD line crosses below its signal line. Bias short.",
  macd_zero:
    "MACD line crosses zero / centerline. Alchemy centerline crossover. Bull = cross above; bear = cross below.",
  macd_zero_bull: "MACD line crosses above zero. Bias long.",
  macd_zero_bear: "MACD line crosses below zero. Bias short.",
  stoch_div:
    "Price and Stochastic %K disagree at two swings. Classic or hidden. Slope osc_line on %K (not %D).",
  stoch_div_bull: "Bullish Stochastic %K divergence. Bias long.",
  stoch_div_bear: "Bearish Stochastic %K divergence. Bias short.",
};

const ENTRY_LABELS: Record<string, string> = {
  signal_close: "Signal close",
  signal_open: "Signal open",
  next_open: "Next open",
  break_close: "Break close",
  retest_then_close: "Retest then close",
  rejection: "Rejection",
  limit_at_level: "Limit at level",
};

const ENTRY_DEFS: Record<string, string> = {
  signal_close: "Enter at the close of the setup / signal bar.",
  signal_open: "Enter at the open of the setup / signal bar.",
  next_open: "Enter at the open of the bar after the signal.",
  break_close:
    "Enter on the close of the first candle that closes beyond the level (Long above / Short below). Break and Enter are the same bar. A wick alone does not count. If that close never prints in the window, the path is incomplete. No entry.",
  retest_then_close:
    "After a break, price must overlap the setup zone, then a later candle closes as confirmation (not a loose edge wick). That confirm close is the entry. If price closes through the opposite side first, the retest is invalidated. No entry.",
  rejection:
    "Touch or pierce the level, then close back on the fade side. No breakout required. Short: high ≥ level and close < level. Long: low ≤ level and close > level. Zone (Level=Both): shorts reject the high, longs reject the low. Waits up to Entry → Max wait from the setup close (empty = 24 bars); incomplete when no rejection prints.",
  limit_at_level: "Place a limit order at the setup level (often the gap midpoint).",
  rule: "Custom entry rule: edit it under Rules → Entry. Zones and levels of the recipe are context; the rule decides the fill.",
};

/** Short Interaction meaning under a location chip. */
export function strategyRdLocationInteractionMeaning(
  conceptId: string | null | undefined,
  interaction: string | null | undefined
): string | null {
  const id = (conceptId || "").trim().toLowerCase();
  const ix = (interaction || "").trim().toLowerCase() || "respect";
  if (id === "fvg_overlap" || id.includes("fvg")) {
    return ix === "reject"
      ? "Break through the opposing FVG. Long fills on a close above a bearish zone."
      : "Tag FVG and hold in favor of Trade side.";
  }
  if (id === "near_ob" || id.includes("order_block") || /(^|_)ob(_|$)/.test(id)) {
    return ix === "reject"
      ? "Break through the opposing order block. Long fills on a close above a bearish zone."
      : "Tag OB and hold in favor of Trade side.";
  }
  if (id === "near_breaker") {
    return ix === "reject"
      ? "Break through the opposing breaker. Long fills on a close above a bearish zone."
      : "Retest failed OB from the flipped side and hold.";
  }
  if (id === "near_swing") {
    if (ix === "break" || ix === "reject") {
      return "Close through the fractal swing in Trade side direction.";
    }
    return "Classic wick reclaim: tag the fractal swing and hold.";
  }
  if (id === "near_prior_sr") {
    if (ix === "break" || ix === "reject") {
      return "Close through prior day/week/month H/L in Trade side direction.";
    }
    return "Classic wick reclaim: tag prior calendar H/L (not a fractal swing) and hold.";
  }
  if (id === "prior_liq_run" || id === "liq_run" || (id.includes("liq") && id.includes("run"))) {
    return ix === "reject"
      ? "Failed opposing run: the other side's run fails before entry (confirm only)."
      : "Same-direction liquidity run (filter only, not the Enter bar).";
  }
  if (
    id === "prior_liq_sweep" ||
    id === "liq_sweep" ||
    id.includes("sweep") ||
    (id.includes("liq") && !id.includes("run"))
  ) {
    return ix === "reject"
      ? "Failed opposing grab: the other side's sweep fails before entry (confirm only)."
      : "Same-direction liquidity sweep (filter only, not the Enter bar).";
  }
  if (id === "bos" || id === "choch") {
    return ix === "reject"
      ? "Failed opposing structure break before entry (confirm only, not the Enter bar)."
      : "Same-direction structure break (filter only, not the Enter bar).";
  }
  if (id === "trendline") {
    if (ix === "break") return "Close through the line in Trade side direction.";
    if (ix === "break_retest") return "Break, then retest and hold from the far side.";
    return "Tag trendline and hold on the structure side.";
  }
  return null;
}

export function strategyRdLocationInteractionHelper(
  conceptId?: string | null
): string {
  const id = (conceptId || "").trim().toLowerCase();
  if (id === "prior_liq_sweep" || id === "prior_liq_run") {
    return "Timing chooses before the setup, after it, or either. Confirm only: it does not move Enter onto the sweep or run bar.";
  }
  if (id === "bos" || id === "choch") {
    return "Confirm this event before entry. It does not move Enter onto the Liq / BOS / CHoCH bar.";
  }
  if (id === "near_swing" || id === "near_prior_sr") {
    return "Setup must print, then this high/low must be respected or broken before entry.";
  }
  return "Setup must print, then this location must be respected/rejected (or broken) before entry.";
}

/** Optional breaker subtitle under Location chips. */
export function strategyRdLocationBreakerSubtitle(
  conceptId: string | null | undefined
): string | null {
  const id = (conceptId || "").trim().toLowerCase();
  if (id !== "near_breaker") return null;
  return "Breaker = failed order block with flipped role (broken bull OB → bearish resistance; broken bear OB → bullish support).";
}

/** Soft Entry-mode suggestion — hint only, never hard-rewrite Entry. */
export function strategyRdLocationEntryModeHint(
  interaction: string | null | undefined,
  conceptId?: string | null
): string | null {
  const id = (conceptId || "").trim().toLowerCase();
  // Event locations never own Enter — skip zone-style entry hints.
  if (
    id === "prior_liq_sweep" ||
    id === "prior_liq_run" ||
    id === "bos" ||
    id === "choch"
  ) {
    return null;
  }
  const ix = (interaction || "").trim().toLowerCase();
  if (ix === "respect") return "Respect → Entry often feels retest / hold-like on the setup level.";
  if (ix === "reject") {
    if (
      id === "fvg_overlap" ||
      id === "near_ob" ||
      id === "near_breaker" ||
      id.includes("fvg")
    ) {
      return "Break through → long fills on a close above the opposing zone.";
    }
    return "Reject → failed opposing event before entry. It does not move Enter.";
  }
  if (ix === "break") return "Break → Entry often feels like break_close through the line.";
  if (ix === "break_retest") return "Break + retest → Entry often pairs with retest mode.";
  return null;
}

export type StrategyRdConceptDiagramKind =
  | "orb"
  | "pdh_retest"
  | "coil"
  | "engulf"
  | "inside_break"
  | "fvg"
  | "fvg_trend"
  | "sweep_fvg"
  | "ob_fvg"
  | "inverse_fvg"
  | "order_block"
  | "liquidity"
  | "volume"
  | "trend"
  | "generic";

/** Expand HTF / FVG / MA (and similar) in picker and composer labels. Chart pills stay short. */
export function strategyRdExpandConceptPickerLabel(label: string): string {
  let text = label.trim();
  if (!text) return text;
  text = text.replace(/\bSimple Moving Average \(SMA\)/gi, "Simple Moving Average (SMA)");
  text = text.replace(/\bExponential Moving Average \(EMA\)/gi, "Exponential Moving Average (EMA)");
  text = text.replace(/\bSMA(\d+)\b/gi, (_m, n: string) => `${n}-period simple moving average`);
  text = text.replace(/\bEMA(\d+)\b/gi, (_m, n: string) => `${n}-period exponential moving average`);
  text = text.replace(/\bHTF\b/g, "Higher timeframe");
  text = text.replace(/\ban FVG\b/g, "a Fair Value Gap");
  text = text.replace(/\bIFVG\b/g, "Inverse Fair Value Gap");
  text = text.replace(/\bFVG\b/g, "Fair Value Gap");
  text = text.replace(/\bS\/R\b/g, "Support/Resistance");
  text = text.replace(/\bH\/L\b/g, "High/Low");
  text = text.replace(/\bMansfield RS\b/g, "Mansfield Relative Strength");
  text = text.replace(/\bRS vs\b/g, "Relative strength vs");
  text = text.replace(/\b-asset RS\b/g, "-asset relative strength");
  text = text.replace(/\bADR\b/g, "Average Daily Range");
  text = text.replace(/\bATR\b/g, "Average True Range");
  text = text.replace(/\bEOD\b/g, "end of day");
  text = text.replace(/\bBB\b/g, "Bollinger bands");
  text = text.replace(/\bORB\b/g, "opening range");
  text = text.replace(/\bPDH\/PDL\b/g, "previous day High/Low");
  text = text.replace(/\bPDH\b/g, "previous day High");
  text = text.replace(/\bPDL\b/g, "previous day Low");
  text = text.replace(/\bCE\b/g, "gap midpoint");
  text = text.replace(/\ban OB\b/g, "an Order Block");
  text = text.replace(/\bOB\b/g, "Order Block");
  text = text.replace(/% from MA\b/g, "Percent from Moving Average");
  text = text.replace(/\bMA stack\b/g, "Moving Average stack");
  text = text.replace(/\bfrom MA\b/g, "from Moving Average");
  text = text.replace(/\bto MA\b/g, "to Moving Average");
  text = text.replace(/\bMA\b/g, "Moving Average");
  text = text.replace(/\ban fair value gap\b/g, "a Fair Value Gap");
  return text;
}

/** Catalog display name when this id is in the user-facing map; otherwise null. */
export function strategyRdKnownConceptLabel(
  id: string | null | undefined
): string | null {
  const key = (id || "").trim();
  if (!key || !FULL_NAMES[key]) return null;
  return strategyRdExpandConceptPickerLabel(FULL_NAMES[key]);
}

export function strategyRdConceptDisplayName(
  id: string | null | undefined,
  fallback?: string | null
): string {
  const key = (id || "").trim();
  if (!key) {
    const empty = (fallback || "").trim();
    return empty ? strategyRdExpandConceptPickerLabel(empty) : "Concept";
  }
  const catalogName = (fallback || "").trim();
  // Sweep / run display names come from the catalog. Ids stay prior_liq_*.
  if (catalogName && (key === "prior_liq_sweep" || key === "prior_liq_run")) {
    return strategyRdExpandConceptPickerLabel(catalogName);
  }
  if (FULL_NAMES[key]) return strategyRdExpandConceptPickerLabel(FULL_NAMES[key]);
  if (fallback?.trim()) return strategyRdExpandConceptPickerLabel(fallback);
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * HTF bias chip: scope letter + direction (W↓ weekly bear, D↑ daily bull, D· daily neutral).
 * Falls back to Bias↓ / Bias↑ / Neutral when scope is missing.
 */
export function strategyRdHtfBiasChartLabel(
  scope: string | null | undefined,
  bias: string | null | undefined
): string {
  const scopeKey = (scope || "").trim().toLowerCase();
  const biasKey = (bias || "").trim().toLowerCase();
  const arrow =
    biasKey === "bear" || biasKey === "short" || biasKey === "down"
      ? "↓"
      : biasKey === "bull" || biasKey === "long" || biasKey === "up"
        ? "↑"
        : "";
  const letter =
    scopeKey === "week" || scopeKey === "weekly"
      ? "W"
      : scopeKey === "day" || scopeKey === "daily"
        ? "D"
        : scopeKey === "month" || scopeKey === "monthly"
          ? "M"
          : "";
  if (letter && arrow) return `${letter}${arrow}`;
  if (arrow) return `Bias${arrow}`;
  if (biasKey === "neutral") {
    return letter ? `${letter}·` : "Neutral";
  }
  if (letter) return `${letter} bias`;
  return "Bias";
}

/** Short chart tag so CHoCH / BOS / FVG stay readable without covering the candles. */
export function strategyRdConceptChartLabel(
  id: string | null | undefined,
  fallback?: string | null
): string {
  const key = (id || "").trim();
  const lower = key.toLowerCase();
  if (lower && CHART_ABBREVS[lower]) return CHART_ABBREVS[lower];
  // Agnostic / concrete OB + breaker ids that may arrive without underscores.
  if (
    lower === "ob" ||
    lower.startsWith("ob_") ||
    lower.includes("order_block") ||
    /(^|_)order[_-]?block(_|$)/.test(lower)
  ) {
    return "OB";
  }
  if (lower === "breaker" || lower.startsWith("breaker_")) return "BB";
  const blob = `${key} ${fallback || ""}`.toLowerCase();
  if (blob.includes("choch") || blob.includes("change of character")) return "CHoCH";
  if (/(^|_)bos($|_)/.test(lower) || blob.includes("break of structure")) return "BOS";
  if (blob.includes("fair value") || /(^|_)fvg($|_)/.test(lower)) return "FVG";
  if (blob.includes("order block") || blob.includes("order-block")) return "OB";
  if (/\bbreaker\b/.test(blob) && !blob.includes("near")) return "BB";
  const full = strategyRdConceptDisplayName(id, fallback);
  // Never turn generic zone names into "Sr" / "Lr".
  const fullLower = full.toLowerCase();
  if (
    fullLower === "setup range" ||
    fullLower === "level range" ||
    fullLower === "coil range" ||
    fullLower === "order block" ||
    fullLower === "breaker"
  ) {
    if (fullLower.includes("order")) return "OB";
    if (fullLower.includes("breaker")) return "BB";
    if (fullLower.includes("coil")) return "Coil";
    return "Setup";
  }
  const words = full.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    // Prefer a short readable stem over cryptic initials (e.g. "Pin Bar Bull" → "PinB").
    const stem = words[0].replace(/[^A-Za-z0-9&↑↓↔★]/g, "");
    if (stem.length >= 2 && stem.length <= 6) {
      const polarity =
        /\bbull|\bup|\bbottom|\blower/i.test(full) && !/\bbear|\bdown|\btop|\bupper/i.test(full)
          ? "↑"
          : /\bbear|\bdown|\btop|\bupper/i.test(full) && !/\bbull|\bup|\bbottom|\blower/i.test(full)
            ? "↓"
            : "";
      return `${stem.slice(0, 5)}${polarity}`;
    }
    return words
      .map((word) => word[0] || "")
      .join("")
      .slice(0, 5);
  }
  return full.length > 6 ? full.slice(0, 6) : full;
}

export function strategyRdConceptShortDefinition(
  id: string | null | undefined,
  fallback?: string | null
): string {
  const key = (id || "").trim();
  if (key && SHORT_DEFS[key]) return SHORT_DEFS[key];
  const fallbackKey = (fallback || "").trim().toLowerCase().replace(/[^\w]+/g, "_");
  if (fallbackKey && SHORT_DEFS[fallbackKey]) return SHORT_DEFS[fallbackKey];
  if (fallback?.trim()) {
    return fallback
      .trim()
      .replace(/\bORB\b/g, "opening range")
      .replace(/\bPDH\b/g, "previous day high")
      .replace(/\bPDL\b/g, "previous day low")
      .replace(/\bFVG\b/g, "fair value gap")
      .replace(/\bOB\b/g, "order block")
      .replace(/\bCE\b/g, "gap midpoint");
  }
  return "Select a concept to see what it finds and how it relates to Entry Rules.";
}

/** Optional TF tip under the composer description (Stage prefers Daily / Weekly). */
export function strategyRdConceptTfGuidance(
  id: string | null | undefined
): string | null {
  const key = (id || "").trim().toLowerCase();
  if (key === "stage" || key === "stage_2" || key === "stage_4" || /^stage_\d+$/.test(key)) {
    return "Recommend Daily (1D) or Weekly when available. SMA 30 on 15m is not Weinstein’s 30-week MA.";
  }
  return null;
}

/**
 * First-timer “What” copy for concepts where catalog text is too terse.
 * Use / Recommend from the BE catalog are kept as-is.
 */
const FE_WHAT_COPY: Record<string, string> = {
  rsi_div:
    "RSI divergence looks for two swings where price and the RSI line disagree. A classic early warning that momentum is not confirming the move. Classic (regular): price makes a lower low while RSI makes a higher low (bullish / long bias), or price a higher high while RSI a lower high (bearish / short bias). Hidden: the slopes flip: often used as a continuation cue in the existing trend. On the chart, connect the two price swings and the two RSI points at the same bars; if those slopes disagree the way the label says, the setup is confirmed. Enter is the signal bar a few candles after the second swing (not the swing itself).",
  rsi_div_bull:
    "Bullish RSI divergence: at two lows, price and RSI disagree in a way that favors longs. Classic = price lower-low + RSI higher-low (possible reversal up). Hidden = price higher-low + RSI lower-low (possible continuation up). Confirm on the chart by comparing the price slope to the RSI slope; Enter is the later signal bar.",
  rsi_div_bear:
    "Bearish RSI divergence: at two highs, price and RSI disagree in a way that favors shorts. Classic = price higher-high + RSI lower-high (possible reversal down). Hidden = price lower-high + RSI higher-high (possible continuation down). Confirm on the chart by comparing the price slope to the RSI slope; Enter is the later signal bar.",
  macd_div:
    "MACD divergence is the same idea as RSI divergence, but the oscillator pane is MACD. Default: slope on the MACD line (Alchemy chart examples). Optional source=hist for histogram divergences. Classic = reversal bias when slopes disagree; hidden = continuation. Enter is the signal bar after the second swing. Do not treat MACD×signal crosses as part of the divergence.",
  macd_div_bull:
    "Bullish MACD divergence: price and MACD disagree at two lows in a bullish way (classic or hidden). Confirm slopes on the chart; Enter is the later signal bar.",
  macd_div_bear:
    "Bearish MACD divergence: price and MACD disagree at two highs in a bearish way (classic or hidden). Confirm slopes on the chart; Enter is the later signal bar.",
  macd_cross:
    "MACD signal-line cross: the MACD line crosses its signal line. Bullish = cross above; bearish = cross below. On the chart, show the MACD pane and mark Enter + Cross on the signal bar. No divergence slopes.",
  macd_cross_bull:
    "Bullish MACD signal-line cross: MACD line crosses above signal. Mark Enter + Cross on that bar.",
  macd_cross_bear:
    "Bearish MACD signal-line cross: MACD line crosses below signal. Mark Enter + Cross on that bar.",
  macd_zero:
    "MACD zero / centerline cross: the MACD line crosses zero. Bullish = above; bearish = below. Mark Enter + Zero on the signal bar. No divergence slopes.",
  macd_zero_bull:
    "Bullish MACD zero cross: MACD line crosses above zero. Mark Enter + Zero on that bar.",
  macd_zero_bear:
    "Bearish MACD zero cross: MACD line crosses below zero. Mark Enter + Zero on that bar.",
  stoch_div:
    "Stochastic %K divergence: two price swings vs two %K points at the same bars. Classic = slopes disagree (reversal bias); hidden = same-way disagreement (continuation). Always draw osc_line on %K, not %D. Enter is the signal bar after the second swing.",
  stoch_div_bull:
    "Bullish Stochastic %K divergence at two lows. Confirm price vs %K slopes; Enter is the later signal bar.",
  stoch_div_bear:
    "Bearish Stochastic %K divergence at two highs. Confirm price vs %K slopes; Enter is the later signal bar.",
};

function strategyRdConceptFeWhat(id: string | null | undefined): string | null {
  const key = (id || "").trim().toLowerCase();
  if (!key) return null;
  if (FE_WHAT_COPY[key]) return FE_WHAT_COPY[key];
  // Family ids (rsi_div_*) already covered; bare family prefix fallback.
  if (key.includes("rsi_div")) return FE_WHAT_COPY.rsi_div;
  if (key.includes("macd_div")) return FE_WHAT_COPY.macd_div;
  if (key.includes("macd_cross")) return FE_WHAT_COPY.macd_cross;
  if (key.includes("macd_zero")) return FE_WHAT_COPY.macd_zero;
  if (key.includes("stoch_div")) return FE_WHAT_COPY.stoch_div;
  return null;
}

/**
 * Composer description: prefer FE “What” for first-timer clarity when we have it;
 * keep BE Use / Recommend / Detect sections unchanged.
 * A confluence chip uses the catalog sentence for that role as-is.
 */
export function strategyRdConceptDescriptionForDisplay(
  id: string | null | undefined,
  catalogDescription: string | null | undefined,
  role?: string | null
): string {
  const catalog = (catalogDescription || "").trim();
  if ((role || "").trim().toLowerCase() === "confluence") return catalog;
  const feWhat = strategyRdConceptFeWhat(id);
  if (!feWhat) return catalog;
  if (!catalog) return `What: ${feWhat}`;

  const sections = strategyRdConceptDescriptionSections(catalog);
  const hasMarkers = sections.some((s) => s.label !== "");
  if (!hasMarkers) {
    // Plain catalog blurbs — lead with FE What, keep the catalog body as context.
    return `What: ${feWhat}\n\n${catalog}`;
  }

  const parts: string[] = [];
  let wroteWhat = false;
  for (const section of sections) {
    if (section.label === "What") {
      parts.push(`What: ${feWhat}`);
      wroteWhat = true;
      continue;
    }
    if (!section.label) {
      // Unlabeled prefix — drop when we own What (avoid duplicate blurbs).
      continue;
    }
    parts.push(`${section.label}: ${section.text}`);
  }
  if (!wroteWhat) parts.unshift(`What: ${feWhat}`);
  return parts.join("\n\n");
}

/** BE catalog copy sections — `What:` / `Use:` / `Recommend:` (and legacy `Detect:`) — show as-is. */
export type StrategyRdConceptDescriptionSection = {
  label: "What" | "Detect" | "Use" | "Recommend" | "";
  text: string;
};

export function strategyRdConceptDescriptionSections(
  description: string | null | undefined
): StrategyRdConceptDescriptionSection[] {
  const text = (description || "").trim();
  if (!text) return [];

  const markerRe = /(What|Detect|Use|Recommend):\s*/gi;
  const markers: Array<{
    label: "What" | "Detect" | "Use" | "Recommend";
    index: number;
    end: number;
  }> = [];
  let markerMatch: RegExpExecArray | null;
  while ((markerMatch = markerRe.exec(text)) !== null) {
    const raw = String(markerMatch[1] || "").toLowerCase();
    const label: "What" | "Detect" | "Use" | "Recommend" =
      raw === "detect"
        ? "Detect"
        : raw === "use"
          ? "Use"
          : raw === "recommend"
            ? "Recommend"
            : "What";
    markers.push({
      label,
      index: markerMatch.index,
      end: markerMatch.index + markerMatch[0].length,
    });
  }
  if (markers.length === 0) return [{ label: "", text }];

  const sections: StrategyRdConceptDescriptionSection[] = [];
  if (markers[0].index > 0) {
    const prefix = text.slice(0, markers[0].index).trim();
    if (prefix) sections.push({ label: "", text: prefix });
  }
  for (let i = 0; i < markers.length; i++) {
    const start = markers[i].end;
    const stop = i + 1 < markers.length ? markers[i + 1].index : text.length;
    const body = text.slice(start, stop).trim();
    if (!body) continue;
    sections.push({ label: markers[i].label, text: body });
  }
  return sections;
}

/** Compact catalog params for a tooltip (display-only; BE uses defaults). */
export function strategyRdConceptParamsHint(
  params: Record<string, unknown> | null | undefined
): string {
  if (!params) return "";
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      const joined = value.map((item) => String(item)).filter(Boolean).join(", ");
      if (joined) parts.push(`${key}: ${joined}`);
      continue;
    }
    if (typeof value === "object") continue;
    parts.push(`${key}: ${String(value)}`);
  }
  return parts.join(" · ");
}

export function strategyRdEntryModeLabel(mode: string | null | undefined): string {
  const key = (mode || "").trim();
  return ENTRY_LABELS[key] || key.replace(/_/g, " ") || "Entry";
}

export function strategyRdEntryModeDefinition(mode: string | null | undefined): string {
  const key = (mode || "").trim();
  return ENTRY_DEFS[key] || "Entry mode from Rules: decides when Long/Short triggers on the level.";
}

/** Resolve diagram from pack id, setup id, and/or family. */
export function strategyRdConceptDiagramKind(
  id: string | null | undefined,
  family?: string | null,
  altId?: string | null
): StrategyRdConceptDiagramKind {
  const key = `${id || ""} ${altId || ""}`.trim().toLowerCase();
  const fam = (family || "").trim().toLowerCase();

  if (key.includes("inverse") && (key.includes("fvg") || key.includes("fair_value"))) {
    return "inverse_fvg";
  }
  if (
    (key.includes("sweep") || key.includes("liquidity")) &&
    (key.includes("fvg") || key.includes("fair_value"))
  ) {
    return "sweep_fvg";
  }
  if (
    (key.includes("order_block") || key.includes("ob_") || /\bob\b/.test(key)) &&
    (key.includes("fvg") || key.includes("fair_value") || key.includes("overlap"))
  ) {
    return "ob_fvg";
  }
  if (
    (key.includes("fvg") || key.includes("fair_value")) &&
    (key.includes("trend") || key.includes("continuation") || key.includes("continue"))
  ) {
    return "fvg_trend";
  }

  if (
    key.includes("session_first") ||
    key.includes("orb") ||
    key.includes("opening_range") ||
    key.includes("first_candle")
  ) {
    return "orb";
  }
  if (
    key.includes("pdh") ||
    key.includes("pdl") ||
    key.includes("previous_day") ||
    (key.includes("retest") && (key.includes("day") || key.includes("prior")))
  ) {
    return "pdh_retest";
  }
  if (key.includes("coil") || key.includes("contract") || key.includes("expand") || key.includes("squeeze")) {
    return "coil";
  }
  if (key.includes("engulf")) return "engulf";
  if (key.includes("inside")) return "inside_break";
  if (key.includes("fvg") || key.includes("fair_value")) return "fvg";
  if (key.includes("order_block") || key.includes("ob_")) return "order_block";
  if (key === "breaker" || key.startsWith("breaker_")) return "order_block";
  if (key.includes("liquidity") || key.includes("sweep")) return "liquidity";
  if (key.includes("volume")) return "volume";
  if (key.includes("trend")) return "trend";

  if (fam === "ict") return "fvg";
  if (fam === "candle") return "engulf";
  if (fam === "level") return "pdh_retest";
  return "generic";
}
